import React, { useEffect, useState, useCallback } from 'react';
import {
   Box,
   Typography,
   Grid,
   Card,
   CardMedia,
   CardContent,
   CardActions,
   Button,
   CircularProgress,
   Avatar,
   Chip,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   TextField,
   MenuItem,
   Alert,
   AvatarGroup,
   Skeleton,
   IconButton,
   Tabs,
   Tab,
   InputAdornment,
   Tooltip,
   Badge,
   useMediaQuery,
   useTheme,
   Container,
   FormControl,
   Select,
   InputLabel,
} from '@mui/material';
import CreateAlbumDialog from '../components/albums/CreateAlbumDialog';
import { useResponsive, useResponsiveTypography, useResponsiveSpacing } from '../utils/responsive';
import { useUser } from '../contexts/UserContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getDefaultBackgroundStyle } from '../utils/defaultImages';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import TripOriginIcon from '@mui/icons-material/TripOrigin';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import ShareIcon from '@mui/icons-material/Share';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import Menu from '@mui/material/Menu';
import DialogContentText from '@mui/material/DialogContentText';
import { getAuthHeaders } from '../utils/auth';
import { createApiUrl } from '../utils/apiConfig';
// import { getCurrentUserId } from '../utils/auth'; // Assume this exists or use context

interface Album {
   id: string;
   title: string;
   description?: string;
   coverPhotoId?: string;
   coverPhotoUrl?: string;
   memberIds?: string[]; // Legacy field, will be removed
   members?: Array<{
      userId: string;
      email: string;
      displayName?: string;
      photoURL?: string;
      role: 'owner' | 'editor' | 'viewer';
      joinedAt?: any;
   }>;
   userRole?: 'owner' | 'editor' | 'viewer';
   photoIds?: string[];
   photoCount?: number;
   createdAt?: any;
   albumDate?: string;
   tripId?: string;
   eventId?: string;
   source?: 'trip' | 'event' | 'manual';
   tripName?: string;
   eventName?: string;
   isPublic?: boolean;
   type?: 'private' | 'public' | 'paid';
   pricePerPhoto?: number;
   pricingModel?: 'per_photo' | 'one_time' | 'donation';
   createdBy?: string;
   tags?: string[];
}

interface Photo {
   id: string;
   url: string;
   title?: string;
}

const THEMES = [
   'General',
   'Best Friends',
   'Christmas',
   'Birthday',
   'Vacation',
   'Family',
   'Holiday',
   'Other',
];

const AlbumsListPage: React.FC = () => {
   const [albums, setAlbums] = useState<Album[]>([]);
   const [filteredAlbums, setFilteredAlbums] = useState<Album[]>([]);
   const [loading, setLoading] = useState(true);
   const [dialogOpen, setDialogOpen] = useState(false);
   const [createAlbumDialogOpen, setCreateAlbumDialogOpen] = useState(false);
   const [creating, setCreating] = useState(false);
   const [error, setError] = useState<string | null>(null);

   // Add responsive hooks
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
   const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
   const { isMobile: responsiveIsMobile, deviceType } = useResponsive();
   const { fontSize, getFontSize } = useResponsiveTypography();
   const { spacing, getSpacing } = useResponsiveSpacing();

   // Filter and search state
   const [activeTab, setActiveTab] = useState(0);
   const [searchQuery, setSearchQuery] = useState('');
   const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'name' | 'photos'>('name');

   // Album form state
   const [title, setTitle] = useState('');
   const [description, setDescription] = useState('');
   const [albumTheme, setAlbumTheme] = useState('General');
   const [selectedPhotoIds, setSelectedPhotoIds] = useState<string[]>([]);

   const navigate = useNavigate();
   const { currentUser } = useUser();
   const {
      checkSubscriptionStatus,
      setSubscriptionModalOpen,
      currentSubscription,
      hasSubscriptions,
   } = useSubscription();

   // Helper function to check if user has created any albums
   const hasCreatedAnyAlbums = async (): Promise<boolean> => {
      if (!currentUser?.uid) return false;
      try {
         // Query albums collection only
         const albumsQuery = query(
            collection(db, 'albums'),
            where('createdBy', '==', currentUser.uid)
         );
         const albumsSnapshot = await getDocs(albumsQuery);

         return albumsSnapshot.size > 0;
      } catch (error) {
         console.error('Error checking albums creation status:', error);
         return false;
      }
   };

   // Helper function to check if user can create album
   const canCreateAlbum = async (): Promise<boolean> => {
      const hasCreatedAlbums = await hasCreatedAnyAlbums();

      // Check for paid subscription only (not invited access)
      const hasPaidSubscription =
         hasSubscriptions &&
         currentSubscription &&
         (currentSubscription.status === 'active' ||
            currentSubscription.status === 'trialing' ||
            currentSubscription.status === 'past_due');

      // If user hasn't created any albums yet, always allow
      if (!hasCreatedAlbums) {
         return true;
      }

      // If user has created albums, only allow if they have PAID subscription
      return hasPaidSubscription;
   };
   const [searchParams, setSearchParams] = useSearchParams();
   const userId = currentUser?.uid;

   const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
   const [menuAlbumId, setMenuAlbumId] = useState<string | null>(null);
   const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
   const [albumToDelete, setAlbumToDelete] = useState<Album | null>(null);
   const [coverPhotos, setCoverPhotos] = useState<{ [photoId: string]: Photo }>({});

   // Edit album state
   const [editDialogOpen, setEditDialogOpen] = useState(false);
   const [editingAlbum, setEditingAlbum] = useState<Album | null>(null);
   const [editTitle, setEditTitle] = useState('');
   const [editDescription, setEditDescription] = useState('');
   const [editAlbumTheme, setEditAlbumTheme] = useState('General');

   // Tab labels and counts
   const tabData = [
      { label: 'All', count: albums.length },
      { label: 'Trips', count: albums.filter((a) => a.source === 'trip').length },
      { label: 'Events', count: albums.filter((a) => a.source === 'event').length },
      { label: 'Personal', count: albums.filter((a) => a.source === 'manual' || !a.source).length },
      {
         label: 'Shared',
         count: albums.filter(
            (a) => (a.members && a.members.length > 1) || (a.memberIds && a.memberIds.length > 1)
         ).length,
      },
   ];

   // Debug tab counts
   useEffect(() => {
      if (albums.length > 0) {
         console.log('Tab counts debug:', {
            total: albums.length,
            trips: albums.filter((a) => a.source === 'trip').length,
            events: albums.filter((a) => a.source === 'event').length,
            personal: albums.filter((a) => a.source === 'manual' || !a.source).length,
            shared: albums.filter(
               (a) => (a.members && a.members.length > 1) || (a.memberIds && a.memberIds.length > 1)
            ).length,
         });
         console.log(
            'Albums by source:',
            albums.map((a) => ({ title: a.title, source: a.source }))
         );
      }
   }, [albums]);

   useEffect(() => {
      if (!userId) return;
      setLoading(true);
      setError(null);

      const fetchAlbums = async () => {
         try {
            const headers = await getAuthHeaders();

            console.log('🔍 Fetching albums for user:', userId);
            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/albums/user/${userId}`,
               { headers }
            );

            console.log('📡 Albums API response status:', response.status, response.ok);

            if (!response.ok) {
               const errorText = await response.text();
               console.error('❌ Albums API error:', errorText);
               throw new Error(`Failed to fetch albums: ${response.status} ${errorText}`);
            }

            const data = await response.json();
            console.log('✅ Albums received from API:', data);

            // Debug album sources
            if (data.albums && Array.isArray(data.albums)) {
               data.albums.forEach((album: Album) => {
                  console.log(`Album "${album.title}":`, {
                     source: album.source,
                     tripId: album.tripId,
                     eventId: album.eventId,
                     tripName: album.tripName,
                     eventName: album.eventName,
                  });
               });
               setAlbums(data.albums);
            } else {
               console.warn('⚠️ No albums found or invalid response format');
               setAlbums([]);
            }
         } catch (error) {
            console.error('❌ Error fetching albums:', error);
            setError(error instanceof Error ? error.message : 'Failed to load albums');
            setAlbums([]);
         } finally {
            setLoading(false);
         }
      };

      fetchAlbums();
   }, [userId]);

   // Filter and sort albums whenever albums, activeTab, searchQuery, or sortBy changes
   useEffect(() => {
      let filtered = [...albums];

      // Apply tab filter
      switch (activeTab) {
         case 1: // Trips
            filtered = filtered.filter((a) => a.source === 'trip');
            break;
         case 2: // Events
            filtered = filtered.filter((a) => a.source === 'event');
            break;
         case 3: // Personal
            filtered = filtered.filter((a) => a.source === 'manual' || !a.source);
            break;
         case 4: // Shared
            filtered = filtered.filter(
               (a) => (a.members && a.members.length > 1) || (a.memberIds && a.memberIds.length > 1)
            );
            break;
         default: // All
            break;
      }

      // Apply search filter
      if (searchQuery.trim()) {
         const query = searchQuery.toLowerCase();
         filtered = filtered.filter(
            (a) =>
               a.title.toLowerCase().includes(query) ||
               (a.description && a.description.toLowerCase().includes(query)) ||
               (a.tripName && a.tripName.toLowerCase().includes(query)) ||
               (a.eventName && a.eventName.toLowerCase().includes(query))
         );
      }

      // Apply sorting
      filtered.sort((a, b) => {
         switch (sortBy) {
            case 'oldest':
               return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            case 'name':
               return a.title.localeCompare(b.title);
            case 'photos':
               return (b.photoIds?.length || 0) - (a.photoIds?.length || 0);
            case 'newest':
            default:
               return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
         }
      });

      setFilteredAlbums(filtered);
   }, [albums, activeTab, searchQuery, sortBy]);

   // Fetch cover photos for all albums after albums are loaded (explicit cover + auto-place first photo)
   useEffect(() => {
      const photoIdsToFetch = new Set<string>();
      albums.forEach((album) => {
         if (album.coverPhotoId) {
            photoIdsToFetch.add(album.coverPhotoId);
         } else if (
            !album.coverPhotoUrl &&
            album.photoIds &&
            album.photoIds.length > 0
         ) {
            // Auto-place: use first photo as cover when no explicit cover is set
            photoIdsToFetch.add(album.photoIds[0]);
         }
      });
      const allPhotoIds = Array.from(photoIdsToFetch);
      if (allPhotoIds.length === 0) {
         setCoverPhotos({});
         return;
      }
      import('../utils/auth')
         .then(({ getAuthHeaders }) => getAuthHeaders())
         .then((headers) =>
            fetch(`${process.env.REACT_APP_API_URL}/api/albums/photos/batch`, {
               method: 'POST',
               headers,
               body: JSON.stringify({ photoIds: allPhotoIds }),
            })
         )
         .then((res) => res.json())
         .then((data) => {
            // data.photos should be an array of { id, url, ... }
            const map: { [photoId: string]: Photo } = {};
            (data.photos || []).forEach((photo: Photo) => {
               map[photo.id] = photo;
            });
            setCoverPhotos(map);
         })
         .catch(() => setCoverPhotos({}));
   }, [albums]);

   const handleOpenDialog = useCallback(() => {
      setTitle('');
      setDescription('');
      setAlbumTheme('General');
      setSelectedPhotoIds([]);
      setError(null);
      setCreateAlbumDialogOpen(true);
   }, []);

   // Sync tab and sort from URL (shareable / back-forward navigation)
   useEffect(() => {
      const tabParam = searchParams.get('tab');
      const sortParam = searchParams.get('sort');
      if (tabParam) {
         const tabMap: Record<string, number> = {
            all: 0,
            trips: 1,
            events: 2,
            personal: 3,
            shared: 4,
         };
         const value = tabMap[tabParam];
         if (value !== undefined) setActiveTab(value);
      }
      if (
         sortParam &&
         (sortParam === 'newest' ||
            sortParam === 'oldest' ||
            sortParam === 'name' ||
            sortParam === 'photos')
      ) {
         setSortBy(sortParam);
      }
   }, [searchParams]);

   // Effect to auto-open create dialog if create=true is in URL
   useEffect(() => {
      const createParam = searchParams.get('create');
      if (createParam === 'true' && !dialogOpen) {
         handleOpenDialog();
         setSearchParams((prev) => {
            const p = new URLSearchParams(prev);
            p.delete('create');
            return p;
         });
      }
   }, [searchParams, dialogOpen, handleOpenDialog, setSearchParams]);

   const handleCloseDialog = () => {
      setDialogOpen(false);
      setError(null);
   };

   const handleCreateAlbum = async (albumData: {
      title: string;
      description?: string;
      type: 'private' | 'public' | 'paid';
      enableParticipantParticipation: boolean;
      pricePerPhoto?: number;
      pricingModel?: 'per_photo' | 'one_time' | 'donation';
      paidAlbumAccessType?: 'public' | 'private';
      albumDate?: string;
   }) => {
      if (!userId) {
         throw new Error('User not found');
      }

      // Check if user can create album using the new logic
      // const canCreate = await canCreateAlbum();
      // if (!canCreate) {
      //    setSubscriptionModalOpen(true);

      //    throw new Error('Subscription required to create album');
      // }

      setCreating(true);
      setError(null);
      try {
         const headers = await getAuthHeaders();

         // Create album with JSON
         const payload = {
            title: albumData.title,
            description: albumData.description || '',
            type: albumData.type,
            enableParticipantParticipation: albumData.enableParticipantParticipation,
            createdBy: userId,
            albumDate: albumData.albumDate,
            ...(albumData.type === 'paid' && {
               pricePerPhoto: albumData.pricePerPhoto || 5.0,
               pricingModel: albumData.pricingModel || 'per_photo',
               paidAlbumAccessType: albumData.paidAlbumAccessType || 'public',
            }),
         };

         console.log('🔍 [AlbumsListPage] Creating album with payload:', payload);

         const res = await fetch(`${process.env.REACT_APP_API_URL}/api/albums/create`, {
            method: 'POST',
            headers: {
               ...headers,
               'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
         });
         const data = await res.json();
         if (!res.ok) throw new Error(data.message || 'Failed to create album');

         // Refresh albums with authentication headers
         const refreshResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/user/${userId}`,
            { headers }
         );

         if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();
            setAlbums(refreshData.albums || []);
         }
      } catch (err: any) {
         throw err;
      } finally {
         setCreating(false);
      }
   };

   // Helper to get cover photo URL (explicit cover or auto-placed first photo)
   const getCoverPhotoUrl = (album: Album) => {
      // Prioritize album.coverPhotoUrl from Firestore (same logic as AlbumDetailPage)
      if (album.coverPhotoUrl) {
         return album.coverPhotoUrl;
      }
      // Explicit cover photo from batch API
      const coverId = album.coverPhotoId;
      if (coverId && coverPhotos[coverId]) {
         return coverPhotos[coverId].url;
      }
      // Auto-place: use first photo as cover when no explicit cover is set
      const firstPhotoId = album.photoIds?.[0];
      if (firstPhotoId && coverPhotos[firstPhotoId]) {
         return coverPhotos[firstPhotoId].url;
      }
      return '';
   };

   // Helper to get source icon
   const getSourceIcon = (source?: string) => {
      switch (source) {
         case 'trip':
            return <TripOriginIcon sx={{ fontSize: 16 }} />;
         case 'event':
            return <EventIcon sx={{ fontSize: 16 }} />;
         default:
            return <PersonIcon sx={{ fontSize: 16 }} />;
      }
   };

   // Helper to get source color
   const getSourceColor = (source?: string) => {
      switch (source) {
         case 'trip':
            return 'info';
         case 'event':
            return 'warning';
         default:
            return 'default';
      }
   };

   // Helper to get left border accent color by source (trip=primary, event=secondary, personal=grey)
   const getSourceBorderColor = (source?: string) => {
      switch (source) {
         case 'trip':
            return 'primary.main';
         case 'event':
            return 'secondary.main';
         default:
            return 'grey.400';
      }
   };

   // Helper to get source label
   const getSourceLabel = (album: Album) => {
      if (album.tripName) return album.tripName;
      if (album.eventName) return album.eventName;
      if (album.source === 'trip') return 'Trip Album';
      if (album.source === 'event') return 'Event Album';
      return 'Personal Album';
   };

   const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
      const tabSlug = ['all', 'trips', 'events', 'personal', 'shared'][newValue];
      setSearchParams((prev) => {
         const p = new URLSearchParams(prev);
         p.set('tab', tabSlug);
         return p;
      });
   };

   const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, albumId: string) => {
      setMenuAnchorEl(event.currentTarget);
      setMenuAlbumId(albumId);
   };

   const handleMenuClose = () => {
      setMenuAnchorEl(null);
      setMenuAlbumId(null);
   };

   const handleDeleteAlbum = async () => {
      if (!albumToDelete || !currentUser) return;
      setCreating(true);
      try {
         const apiUrl = createApiUrl(`/api/albums/${albumToDelete.id}?userId=${currentUser.uid}`);

         console.log('🗑️ Deleting album:', albumToDelete.id, 'API URL:', apiUrl);

         await fetch(apiUrl, {
            method: 'DELETE',
         });
         setDeleteDialogOpen(false);
         setAlbumToDelete(null);
         // Refresh albums
         setLoading(true);
         const refreshHeaders = await getAuthHeaders();
         fetch(`${process.env.REACT_APP_API_URL}/api/albums/user/${userId}`, {
            headers: refreshHeaders,
         })
            .then((res) => res.json())
            .then((data) => {
               setAlbums(data.albums || []);
               setLoading(false);
            })
            .catch(() => setLoading(false));
      } catch (err) {
         setError('Failed to delete album');
         setCreating(false);
      }
   };

   const handleEditAlbum = (album: Album) => {
      setEditingAlbum(album);
      setEditTitle(album.title);
      setEditDescription(album.description || '');
      setEditAlbumTheme('General'); // Default theme since it's not stored in the album
      setError(null);
      setEditDialogOpen(true);
   };

   const handleCloseEditDialog = () => {
      setEditDialogOpen(false);
      setEditingAlbum(null);
      setError(null);
   };

   const handleUpdateAlbum = async () => {
      if (!editTitle.trim()) {
         setError('Title is required');
         return;
      }
      if (!editingAlbum) {
         setError('No album selected');
         return;
      }
      setCreating(true);
      setError(null);
      try {
         const headers = await getAuthHeaders();

         const res = await fetch(`${process.env.REACT_APP_API_URL}/api/albums/${editingAlbum.id}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({
               title: editTitle,
               description: editDescription,
            }),
         });
         const data = await res.json();
         if (!res.ok) throw new Error(data.message || 'Failed to update album');
         setEditDialogOpen(false);
         setEditingAlbum(null);
         // Refresh albums with authentication
         const refreshHeaders = await getAuthHeaders();
         const refreshResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/user/${userId}`,
            { headers: refreshHeaders }
         );
         if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();
            setAlbums(refreshData.albums || []);
         }
      } catch (err: any) {
         setError(err.message || 'Failed to update album');
      } finally {
         setCreating(false);
      }
   };

   return (
      <Container
         maxWidth='xl'
         sx={{
            py: isMobile ? 2 : 4,
            px: isMobile ? 1 : 2,
            width: '100%',
            maxWidth: '100vw',
            overflowX: 'hidden',
         }}
      >
         {/* Header */}
         <Box
            sx={{
               display: 'flex',
               justifyContent: 'space-between',
               alignItems: isMobile ? 'flex-start' : 'center',
               mb: isMobile ? 2 : 3,
               flexDirection: isMobile ? 'column' : 'row',
               gap: isMobile ? 2 : 0,
            }}
         >
            <Box>
               <Typography
                  variant='h4'
                  fontWeight={700}
                  sx={{
                     fontSize: isMobile ? fontSize.h4 : fontSize.h3,
                     textAlign: isMobile ? 'center' : 'left',
                  }}
               >
                  Photo Albums
               </Typography>
               <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                  Collect and share moments from trips and events.
               </Typography>
            </Box>
            <Button
               variant='contained'
               startIcon={<AddPhotoAlternateIcon />}
               onClick={async () => {
                  if (await canCreateAlbum()) {
                     handleOpenDialog();
                  } else {
                     setSubscriptionModalOpen(true);
                  }
               }}
               size={isMobile ? 'large' : 'medium'}
               sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  minHeight: isMobile ? '48px' : '40px',
                  px: isMobile ? 3 : 2,
                  fontSize: isMobile ? fontSize.body1 : 'inherit',
                  width: isMobile ? '100%' : 'auto',
                  background: theme.palette.primary.main,
                  boxShadow: `0 4px 15px ${theme.palette.primary.main}4D`,
                  '&:hover': {
                     background: theme.palette.primary.light,
                     boxShadow: `0 6px 20px ${theme.palette.primary.main}66`,
                  },
               }}
            >
               Create Album
            </Button>
         </Box>

         <Box sx={{ mb: isMobile ? 2 : 3, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
            <Tabs
               value={activeTab}
               onChange={handleTabChange}
               variant={isMobile ? 'scrollable' : 'scrollable'}
               scrollButtons='auto'
               sx={{
                  mb: isMobile ? 1 : 2,
                  '& .MuiTab-root': {
                     minHeight: isMobile ? '48px' : '40px',
                     fontSize: isMobile ? fontSize.body2 : 'inherit',
                     px: isMobile ? 1 : 2,
                  },
               }}
            >
               {tabData.map((tab, index) => (
                  <Tab
                     key={tab.label}
                     label={
                        <Badge badgeContent={tab.count} color='primary' max={999}>
                           <Box
                              sx={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 gap: isMobile ? 0.5 : 1,
                                 flexDirection: isMobile ? 'column' : 'row',
                              }}
                           >
                              {index === 1 && (
                                 <TripOriginIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                              )}
                              {index === 2 && <EventIcon sx={{ fontSize: isMobile ? 16 : 18 }} />}
                              {index === 3 && <PersonIcon sx={{ fontSize: isMobile ? 16 : 18 }} />}
                              {index === 4 && <ShareIcon sx={{ fontSize: isMobile ? 16 : 18 }} />}
                              <Typography sx={{ fontSize: isMobile ? fontSize.body2 : 'inherit' }}>
                                 {tab.label}
                              </Typography>
                           </Box>
                        </Badge>
                     }
                  />
               ))}
            </Tabs>

            {/* Search and Sort Controls */}
            <Box
               sx={{
                  display: 'flex',
                  gap: isMobile ? 1 : 2,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  flexDirection: isMobile ? 'column' : 'row',
                  width: '100%',
                  maxWidth: '100%',
               }}
            >
               <TextField
                  placeholder='Search albums, trips, events...'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size={isMobile ? 'medium' : 'small'}
                  sx={{
                     width: isMobile ? '100%' : 'auto',
                     minWidth: isMobile ? 'unset' : 250,
                     maxWidth: '100%',
                     flexGrow: isMobile ? 0 : 1,
                     '& .MuiInputBase-root': {
                        minHeight: isMobile ? '48px' : '40px',
                        fontSize: isMobile ? fontSize.body1 : 'inherit',
                        color: theme.palette.text.primary,
                        '& input': {
                           color: theme.palette.text.primary,
                           '&::placeholder': {
                              color: theme.palette.text.secondary,
                              opacity: 1,
                           },
                        },
                     },
                     '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.divider,
                     },
                     '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.divider,
                     },
                     '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                     },
                  }}
                  InputProps={{
                     startAdornment: (
                        <InputAdornment position='start'>
                           <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                        </InputAdornment>
                     ),
                  }}
               />
               <FormControl size={isMobile ? 'medium' : 'small'} sx={{ minWidth: isMobile ? '100%' : 160 }}>
                  <InputLabel id='albums-sort-label' sx={{ color: theme.palette.text.secondary }}>Sort by</InputLabel>
                  <Select
                     labelId='albums-sort-label'
                     id='albums-sort'
                     value={sortBy}
                     label='Sort by'
                     onChange={(e) => {
                        const value = e.target.value as typeof sortBy;
                        setSortBy(value);
                        setSearchParams((prev) => {
                           const p = new URLSearchParams(prev);
                           p.set('sort', value);
                           return p;
                        });
                     }}
                     sx={{
                        minHeight: isMobile ? '48px' : '40px',
                        fontSize: isMobile ? fontSize.body1 : 'inherit',
                        color: theme.palette.text.primary,
                        '& .MuiSelect-select': {
                           color: theme.palette.text.primary,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                           borderColor: theme.palette.divider,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                           borderColor: theme.palette.divider,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                           borderColor: theme.palette.primary.main,
                        },
                        '& .MuiSvgIcon-root': {
                           color: theme.palette.text.secondary,
                        },
                     }}
                     MenuProps={{
                        PaperProps: {
                           sx: {
                              backgroundColor: theme.palette.background.paper,
                              '& .MuiMenuItem-root': {
                                 color: theme.palette.text.primary,
                                 '&:hover': {
                                    backgroundColor: theme.palette.action.hover,
                                 },
                              },
                           },
                        },
                     }}
                  >
                     <MenuItem value='newest'>Newest</MenuItem>
                     <MenuItem value='oldest'>Oldest</MenuItem>
                     <MenuItem value='name'>Name (A–Z)</MenuItem>
                     <MenuItem value='photos'>Most photos</MenuItem>
                  </Select>
               </FormControl>
            </Box>
         </Box>

         {/* Error Display */}
         {error && (
            <Alert severity='error' sx={{ mb: 3 }} onClose={() => setError(null)}>
               {error}
            </Alert>
         )}

         {/* Albums Grid */}
         {loading ? (
            <Grid container spacing={isMobile ? 2 : 3} sx={{ width: '100%', margin: 0 }}>
               {[...Array(8)].map((_, i) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                     <Skeleton
                        variant='rectangular'
                        height={isMobile ? 240 : 280}
                        sx={{ borderRadius: 2 }}
                     />
                  </Grid>
               ))}
            </Grid>
         ) : filteredAlbums.length === 0 ? (
            <Box
               sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  py: isMobile ? 4 : 8,
                  color: theme.palette.text.secondary,
                  px: isMobile ? 2 : 0,
               }}
            >
               <ImageNotSupportedIcon sx={{ fontSize: isMobile ? 48 : 72, mb: 2 }} />
               <Typography
                  variant='h6'
                  color='text.secondary'
                  sx={{
                     mb: 2,
                     fontSize: isMobile ? fontSize.h4 : fontSize.h3,
                     textAlign: 'center',
                  }}
               >
                  {searchQuery
                     ? `No albums match "${searchQuery}". Try a different search.`
                     : activeTab === 0
                       ? 'Create your first album to collect and share photos from trips and events.'
                       : `No ${tabData[activeTab].label.toLowerCase()} albums yet. Create one or switch to All to see everything.`}
               </Typography>
               {!searchQuery && activeTab === 0 && (
                  <Button
                     variant='contained'
                     startIcon={<AddPhotoAlternateIcon />}
                     onClick={async () => {
                        if (await canCreateAlbum()) {
                           handleOpenDialog();
                        } else {
                           setSubscriptionModalOpen(true);
                        }
                     }}
                     size={isMobile ? 'large' : 'medium'}
                     sx={{
                        borderRadius: 2,
                        fontWeight: 600,
                        minHeight: isMobile ? '48px' : '40px',
                        px: isMobile ? 3 : 2,
                        fontSize: isMobile ? fontSize.body1 : 'inherit',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        boxShadow: `0 4px 15px ${theme.palette.primary.main}4D`,
                        '&:hover': {
                           background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.dark} 100%)`,
                           boxShadow: `0 6px 20px ${theme.palette.primary.main}66`,
                        },
                     }}
                  >
                     Create Album
                  </Button>
               )}
            </Box>
         ) : (
            <Grid container spacing={isMobile ? 2 : 3} sx={{ width: '100%', margin: 0 }}>
               {filteredAlbums.map((album) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={album.id}>
                     <Card
                        onClick={() => navigate(`/albums/${album.id}`)}
                        sx={{
                           cursor: 'pointer',
                           height: '100%',
                           borderRadius: 2,
                           boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                           border: '1px solid',
                           borderColor: 'divider',
                           borderLeft: '4px solid',
                           borderLeftColor: getSourceBorderColor(album.source),
                           transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                           '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: 4,
                           },
                           position: 'relative',
                        }}
                     >
                        {/* Privacy indicator */}
                        <Box
                           sx={{
                              position: 'absolute',
                              top: isMobile ? 4 : 8,
                              left: isMobile ? 4 : 8,
                              zIndex: 2,
                           }}
                        >
                           <Tooltip 
                              title={
                                 album.type === 'paid' 
                                    ? 'Paid Album' 
                                    : album.type === 'public' 
                                       ? 'Public Album' 
                                       : 'Private Album'
                              }
                           >
                              <Chip
                                 icon={
                                    album.type === 'paid' 
                                       ? <LockIcon /> 
                                       : album.type === 'public' 
                                          ? <PublicIcon /> 
                                          : <LockIcon />
                                 }
                                 label={
                                    album.type === 'paid' 
                                       ? 'Paid' 
                                       : album.type === 'public' 
                                          ? 'Public' 
                                          : 'Private'
                                 }
                                 size={isMobile ? 'small' : 'small'}
                                 color={
                                    album.type === 'paid' 
                                       ? 'warning' 
                                       : album.type === 'public' 
                                          ? 'success' 
                                          : 'default'
                                 }
                                 sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    backdropFilter: 'blur(4px)',
                                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                                    height: isMobile ? '24px' : '28px',
                                 }}
                              />
                           </Tooltip>
                        </Box>

                        {/* More menu - Only show for owners and editors */}
                        {(album.userRole === 'owner' ||
                           album.userRole === 'editor' ||
                           album.createdBy === userId) && (
                           <IconButton
                              aria-label='more'
                              size={isMobile ? 'medium' : 'small'}
                              sx={{
                                 position: 'absolute',
                                 top: isMobile ? 4 : 8,
                                 right: isMobile ? 4 : 8,
                                 zIndex: 2,
                                 backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                 minHeight: isMobile ? '40px' : '32px',
                                 minWidth: isMobile ? '40px' : '32px',
                              }}
                              onClick={(e) => {
                                 e.stopPropagation();
                                 handleMenuOpen(e, album.id);
                              }}
                           >
                              <MoreVertIcon />
                           </IconButton>
                        )}

                        {/* Cover Image */}
                        <Box
                           sx={{
                              height: isMobile ? 140 : 180,
                              position: 'relative',
                              ...getDefaultBackgroundStyle(
                                 'album',
                                 album.id,
                                 getCoverPhotoUrl(album)
                              ),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                           }}
                        >
                           {!getCoverPhotoUrl(album) && (
                              <>
                                 <Box
                                    sx={{
                                       position: 'absolute',
                                       top: 0,
                                       left: 0,
                                       right: 0,
                                       bottom: 0,
                                       background: 'rgba(0, 0, 0, 0.3)',
                                       zIndex: 1,
                                    }}
                                 />
                                 <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 2 }}>
                                    <InsertPhotoIcon
                                       sx={{
                                          fontSize: isMobile ? 32 : 48,
                                          color: 'white',
                                          mb: 1,
                                          opacity: 0.9,
                                       }}
                                    />
                                 </Box>
                              </>
                           )}
                        </Box>

                        <CardContent sx={{ pb: 1, p: isMobile ? 1.5 : 2 }}>
                           {/* Source indicator */}
                           <Box
                              sx={{
                                 display: 'flex',
                                 justifyContent: 'space-between',
                                 alignItems: 'flex-start',
                                 mb: isMobile ? 0.5 : 1,
                              }}
                           >
                              <Chip
                                 icon={getSourceIcon(album.source)}
                                 label={getSourceLabel(album)}
                                 size={isMobile ? 'small' : 'small'}
                                 color={getSourceColor(album.source) as any}
                                 variant='outlined'
                                 sx={{
                                    maxWidth: '100%',
                                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                                    height: isMobile ? '24px' : '28px',
                                 }}
                              />
                           </Box>

                           <Typography
                              variant='h6'
                              noWrap
                              fontWeight={600}
                              sx={{
                                 mb: isMobile ? 0.25 : 0.5,
                                 fontSize: isMobile ? fontSize.body1 : fontSize.h4,
                              }}
                           >
                              {album.title}
                           </Typography>

                           <Typography
                              variant='body2'
                              color='text.secondary'
                              sx={{
                                 display: '-webkit-box',
                                 WebkitLineClamp: isMobile ? 1 : 2,
                                 WebkitBoxOrient: 'vertical',
                                 overflow: 'hidden',
                                 minHeight: isMobile ? '1.2em' : '2.5em',
                                 mb: isMobile ? 0.5 : 1,
                                 fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                              }}
                           >
                              {album.description || 'No description'}
                           </Typography>

                           {/* Album Date */}
                           {album.albumDate && (
                              <Typography
                                 variant='caption'
                                 color='text.secondary'
                                 sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    mb: isMobile ? 0.5 : 1,
                                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                                 }}
                              >
                                 <EventIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
                                 {new Date(album.albumDate).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                 })}
                              </Typography>
                           )}

                           {/* Stats and Role */}
                           <Box
                              sx={{
                                 display: 'flex',
                                 gap: isMobile ? 0.5 : 1,
                                 mb: isMobile ? 0.5 : 1,
                                 flexWrap: 'wrap',
                              }}
                           >
                              <Chip
                                 label={`${(album.photoCount ?? album.photoIds?.length) || 0} photos`}
                                 size={isMobile ? 'small' : 'small'}
                                 icon={<PhotoLibraryIcon sx={{ fontSize: isMobile ? 14 : 16 }} />}
                                 color='primary'
                                 variant='outlined'
                                 sx={{
                                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                                    height: isMobile ? '20px' : '24px',
                                 }}
                              />
                              <Chip
                                 label={`${album.members?.length || album.memberIds?.length || 1} members`}
                                 size={isMobile ? 'small' : 'small'}
                                 icon={<PeopleAltIcon sx={{ fontSize: isMobile ? 14 : 16 }} />}
                                 color='secondary'
                                 variant='outlined'
                                 sx={{
                                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                                    height: isMobile ? '20px' : '24px',
                                 }}
                              />
                              {album.userRole && (
                                 <Chip
                                    label={
                                       album.userRole.charAt(0).toUpperCase() +
                                       album.userRole.slice(1)
                                    }
                                    size={isMobile ? 'small' : 'small'}
                                    color={
                                       album.userRole === 'owner'
                                          ? 'error'
                                          : album.userRole === 'editor'
                                            ? 'warning'
                                            : 'default'
                                    }
                                    variant='outlined'
                                    sx={{
                                       fontSize: isMobile ? '0.7rem' : '0.75rem',
                                       height: isMobile ? '20px' : '24px',
                                    }}
                                 />
                              )}
                           </Box>

                           {/* Member avatars */}
                           <AvatarGroup
                              max={isMobile ? 3 : 4}
                              sx={{
                                 justifyContent: 'flex-start',
                                 '& .MuiAvatar-root': {
                                    width: isMobile ? 20 : 24,
                                    height: isMobile ? 20 : 24,
                                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                                 },
                              }}
                           >
                              {album.members?.map((member, index) => (
                                 <Avatar
                                    key={member.userId}
                                    src={member.photoURL || undefined}
                                    sx={{
                                       width: isMobile ? 20 : 24,
                                       height: isMobile ? 20 : 24,
                                       fontSize: isMobile ? '0.7rem' : '0.75rem',
                                    }}
                                 >
                                    {(
                                       member.displayName?.[0] ||
                                       member.email?.[0] ||
                                       member.userId?.[0] ||
                                       'U'
                                    )?.toUpperCase()}
                                 </Avatar>
                              )) ||
                                 album.memberIds?.map((id, index) => (
                                    <Avatar
                                       key={id}
                                       sx={{
                                          width: isMobile ? 20 : 24,
                                          height: isMobile ? 20 : 24,
                                          fontSize: isMobile ? '0.7rem' : '0.75rem',
                                       }}
                                    >
                                       {id[0]?.toUpperCase()}
                                    </Avatar>
                                 ))}
                           </AvatarGroup>
                        </CardContent>
                     </Card>
                  </Grid>
               ))}
            </Grid>
         )}
         {/* New Create Album Dialog */}
         <CreateAlbumDialog
            open={createAlbumDialogOpen}
            onClose={() => setCreateAlbumDialogOpen(false)}
            onCreateAlbum={handleCreateAlbum}
         />
         {/* Menu for edit/delete */}
         <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl) && menuAlbumId !== null}
            onClose={handleMenuClose}
         >
            {(() => {
               const album = albums.find((a) => a.id === menuAlbumId);
               const userRole = album?.userRole;
               const isCreator = album?.createdBy === userId;
               const canEdit = userRole === 'owner' || userRole === 'editor' || isCreator;
               const canDelete = userRole === 'owner' || isCreator;

               return (
                  <>
                     {canEdit && (
                        <MenuItem
                           onClick={() => {
                              handleMenuClose();
                              if (album) handleEditAlbum(album);
                           }}
                        >
                           Edit Album
                        </MenuItem>
                     )}
                     {canDelete && (
                        <MenuItem
                           onClick={() => {
                              handleMenuClose();
                              setAlbumToDelete(album || null);
                              setDeleteDialogOpen(true);
                           }}
                        >
                           Delete Album
                        </MenuItem>
                     )}
                     {!canEdit && !canDelete && <MenuItem disabled>View Only</MenuItem>}
                  </>
               );
            })()}
         </Menu>
         {/* Delete confirmation dialog */}
         <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
            <DialogTitle>Delete Album</DialogTitle>
            <DialogContent>
               <DialogContentText>
                  Are you sure you want to delete the album "{albumToDelete?.title}"? This action
                  cannot be undone.
               </DialogContentText>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setDeleteDialogOpen(false)} disabled={creating}>
                  Cancel
               </Button>
               <Button
                  onClick={handleDeleteAlbum}
                  color='error'
                  variant='contained'
                  disabled={creating}
               >
                  {creating ? 'Deleting...' : 'Delete'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Edit Album Dialog */}
         <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth='xs' fullWidth>
            <DialogTitle>Edit Album</DialogTitle>
            <DialogContent>
               {error && (
                  <Alert severity='error' sx={{ mb: 2 }}>
                     {error}
                  </Alert>
               )}
               <TextField
                  label='Title'
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  fullWidth
                  required
                  margin='normal'
                  autoFocus
               />
               <TextField
                  label='Description'
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  fullWidth
                  margin='normal'
                  multiline
                  minRows={2}
               />
            </DialogContent>
            <DialogActions>
               <Button onClick={handleCloseEditDialog} disabled={creating}>
                  Cancel
               </Button>
               <Button
                  onClick={handleUpdateAlbum}
                  variant='contained'
                  color='primary'
                  disabled={creating}
                  sx={{ fontWeight: 600 }}
               >
                  {creating ? 'Updating...' : 'Update'}
               </Button>
            </DialogActions>
         </Dialog>
      </Container>
   );
};

export default AlbumsListPage; 
